import type { GameObj, KEventController } from 'kaplay'
import type { KAPLAYCtx } from '../types.ts'
import { useMusic } from '../music.ts'
import { useGameListPopup } from './game-list-popup.ts'
import { getTiledSize } from '../utils.ts'

type ControllerButton = 'up' | 'right' | 'down' | 'left' | null

export function useGameUiControls(k: KAPLAYCtx) {

  const debug = k.debug.inspect && true
  let objButtonMusic: GameObj
  let objButtonList: GameObj
  let objControllerBackground: GameObj
  let objController: GameObj
  // let objButtonUp: GameObj
  // let objButtonRight: GameObj
  // let objButtonDown: GameObj
  // let objButtonLeft: GameObj
  let touchStart: KEventController
  let touchEnd: KEventController

  const { toggleMusic } = useMusic(k)
  const { showListPopup } = useGameListPopup(k)

  k.loadSprite('uiPlayerController', 'sprites/ui-player-controller.png')

  function createGameUiControls() {
    let lastPressedControllerButton: ControllerButton = null

    const circleSize = getTiledSize(2.75)
    const circleSmallSize = circleSize / 2.5
    const circleMargin = getTiledSize(0.25)
    const sen45 = 0.71
    const circlePos = {
      x: k.width() - (circleSize + circleMargin),
      y: k.height() - (circleSize + circleMargin),
    }

    const upPoligon = k.polygon([
      k.vec2(-(circleSmallSize * sen45), -(circleSmallSize * sen45)),
      k.vec2(-(circleSize * sen45), -(circleSize * sen45)),
      k.vec2(0, -circleSize),
      k.vec2(circleSize * sen45, -(circleSize * sen45)),
      k.vec2(circleSmallSize * sen45, -(circleSmallSize * sen45)),
      k.vec2(0, -circleSmallSize),
    ])

    const rightPoligon = k.polygon([
      k.vec2(circleSmallSize * sen45, -(circleSmallSize * sen45)),
      k.vec2(circleSize * sen45, -(circleSize * sen45)),
      k.vec2(circleSize, 0),
      k.vec2(circleSize * sen45, circleSize * sen45),
      k.vec2(circleSmallSize * sen45, circleSmallSize * sen45),
      k.vec2(circleSmallSize, 0),
    ])

    const downPoligon = k.polygon([
      k.vec2(circleSmallSize * sen45, circleSmallSize * sen45),
      k.vec2(circleSize * sen45, circleSize * sen45),
      k.vec2(0, circleSize),
      k.vec2(-(circleSize * sen45), circleSize * sen45),
      k.vec2(-(circleSmallSize * sen45), circleSmallSize * sen45),
      k.vec2(0, circleSmallSize),
    ])

    const leftPoligon = k.polygon([
      k.vec2(-(circleSmallSize * sen45), circleSmallSize * sen45),
      k.vec2(-(circleSize * sen45), circleSize * sen45),
      k.vec2(-circleSize, 0),
      k.vec2(-(circleSize * sen45), -(circleSize * sen45)),
      k.vec2(-(circleSmallSize * sen45), -(circleSmallSize * sen45)),
      k.vec2(-circleSmallSize, 0),
    ])

    objControllerBackground = k.add([
      k.pos(circlePos.x, circlePos.y),
      k.circle(circleSize),
      k.fixed(),
      k.anchor('center'),
      k.color(k.BLACK),
      k.opacity(0.15),
      k.layer('ui'),
      'game_ui_controller_bg',
      'game_ui',
    ])

    objController = k.add([
      k.sprite('uiPlayerController'),
      k.pos(k.width(), k.height()),
      k.fixed(),
      k.scale(2),
      k.anchor('botright'),
      k.layer('ui'),
      'game_ui_controller',
      'game_ui',
    ])

    /*
    // UP
    objButtonUp = k.add([
      upPoligon,
      k.pos(circlePos.x, circlePos.y),
      k.fixed(),
      k.area(),
      k.layer('ui'),
      k.color(k.Color.BLUE),
      k.opacity(0.2),
      'game_ui_controller_move_up',
      'game_ui_controller_move',
      'game_ui',
    ])

    // RIGHT
    objButtonRight = k.add([
      rightPoligon,
      k.pos(circlePos.x, circlePos.y),
      k.fixed(),
      k.area(),
      k.layer('ui'),
      k.color(k.Color.RED),
      k.opacity(0.2),
      'game_ui_controller_move_right',
      'game_ui_controller_move',
      'game_ui',
    ])

    // BOTTOM
    objButtonDown = k.add([
      downPoligon,
      k.pos(circlePos.x, circlePos.y),
      k.fixed(),
      k.area(),
      k.layer('ui'),
      k.color(k.Color.GREEN),
      k.opacity(0.2),
      'game_ui_controller_move_down',
      'game_ui_controller_move',
      'game_ui',
    ])

    // LEFT
    objButtonLeft = k.add([
      leftPoligon,
      k.pos(circlePos.x, circlePos.y),
      k.fixed(),
      k.area(),
      k.color(k.Color.YELLOW),
      k.opacity(0.2),
      k.layer('player_controls'),
      'game_ui_controller_move_left',
      'game_ui_controller_move',
      'game_ui',
    ])
    */

    const upPoligonArea = upPoligon.renderArea()
    const rightPoligonArea = rightPoligon.renderArea()
    const downPoligonArea = downPoligon.renderArea()
    const leftPoligonArea = leftPoligon.renderArea()

    function pressButton(btn: ControllerButton) {
      if (lastPressedControllerButton) k.releaseButton(lastPressedControllerButton)
      lastPressedControllerButton = btn
      if (btn) k.pressButton(btn)
    }

    touchStart = k.onTouchStart((pos) => {
      const touchOffsetPos = k.vec2(pos.x - circlePos.x, pos.y - circlePos.y)

      let currPressedControllerButton: ControllerButton = null
      if (upPoligonArea.contains(touchOffsetPos)) {
        currPressedControllerButton = 'up'
      } else if (rightPoligonArea.contains(touchOffsetPos)) {
        currPressedControllerButton = 'right'
      } else if (downPoligonArea.contains(touchOffsetPos)) {
        currPressedControllerButton = 'down'
      } else if (leftPoligonArea.contains(touchOffsetPos)) {
        currPressedControllerButton = 'left'
      }

      pressButton(currPressedControllerButton)
    })

    touchEnd = k.onTouchEnd(() => {
      if (lastPressedControllerButton) {
        k.releaseButton(lastPressedControllerButton)
        lastPressedControllerButton = null
      }
    })

    // MUSIC
    objButtonMusic = k.add([
      k.sprite('ui', {
        anim: 'music_on',
        width: getTiledSize(),
        height: getTiledSize(),
      }),
      k.pos(k.width() - getTiledSize(1.5), getTiledSize(0.5)),
      k.area(),
      k.fixed(),
      k.layer('ui'),
      'game_ui_button_music',
      'game_ui_button',
      {
        isToggled: k.store.isMusicPlaying,
        toggleMusic(this: GameObj) {
          toggleMusic()
          this.isToggled = !this.isToggled
          k.store.isMusicPlaying = this.isToggled
          if (this.isToggled) this.play('music_off')
          else this.play('music_on')
        },
      },
    ])
    // objButtonMusic.play('music_on')
    k.onButtonPress('musicToggle', () => {
      objButtonMusic.toggleMusic()
    })
    k.onClick('game_ui_button_music', () => {
      objButtonMusic.toggleMusic()
    })
    if (k.store.isMusicPlaying) {
      // playMusic()
      objButtonMusic.play('music_off')
    }

    // LIST
    objButtonList = k.add([
      k.sprite('ui', {
        anim: 'list',
        width: getTiledSize(),
        height: getTiledSize(),
      }),
      k.pos(k.width() - getTiledSize(3), getTiledSize(0.5)),
      k.area(),
      k.fixed(),
      k.layer('ui'),
      'game_ui_button_list',
      'game_ui_button',
    ])
    k.onClick('game_ui_button_list', () => {
      showListPopup()
    })
  }

  function destroyGameUiControls() {
    touchStart.cancel()
    touchEnd.cancel()
    k.destroy(objButtonMusic)
    k.destroy(objButtonList)
    k.destroy(objControllerBackground)
    k.destroy(objController)
    // k.destroy(objButtonUp)
    // k.destroy(objButtonRight)
    // k.destroy(objButtonDown)
    // k.destroy(objButtonLeft)
  }

  function resizeGameUiControls() {
    if (debug) k.debug.log(`resizeGameUiControls()`)
    destroyGameUiControls()
    createGameUiControls()
  }

  return {
    createGameUiControls,
    resizeGameUiControls,
    destroyGameUiControls,
  }
}
